import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase'; 
import { ArrowRight, Video, Users, Sparkles, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import CONFIG from '../config';

function Home({ userData }) {
  const navigate = useNavigate();
  const [openFAQIndex, setOpenFAQIndex] = useState(null);

  /**
   * handlePurchaseClick:
   * - If user is not logged in, redirect to signup with the plan parameter.
   * - If user is logged in but already has an active tier (lifetime or valid six-month), send them to the dashboard.
   * - Otherwise, navigate to the dedicated PurchaseFlow page.
   */
  const handlePurchaseClick = (planKey) => {
    if (!CONFIG.tiers[planKey]) return;

    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate(`/signup?plan=${planKey}`);
      return;
    }
    
    if (userData) {
      const { subscriptionTier, expirationDate } = userData;
      if (subscriptionTier === 'lifetime') {
        navigate('/dashboard');
        return;
      }
      if (subscriptionTier === 'sixMonth' && expirationDate && expirationDate > Date.now()) {
        navigate('/dashboard');
        return;
      }
    }
    // For one-time tier purchases, always route to the purchase flow page.
    navigate(`/purchase?plan=${planKey}`);
  };

  /**
   * handleCTA:
   * Scrolls to the pricing section.
   */
  const handleCTA = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  const heroTitle = "Stand Out & Get Hired";
  const heroSubtitle = "Let your voice and energy do the talking. A simple video introduction could secure the role you deserve.";

  const features = [
    {
      icon: <Video className="w-6 h-6 text-black" />,
      title: 'Make It Personal',
      text: 'Resumes show what you’ve done. A video shows who you are. Instantly connect with recruiters by letting your personality shine.',
    },
    {
      icon: <Users className="w-6 h-6 text-black" />,
      title: "No Tech Skills Needed",
      text: 'Record from your phone or laptop in minutes. No fancy equipment or editing software required—just genuine, confident introductions.',
    },
    {
      icon: <Sparkles className="w-6 h-6 text-black" />,
      title: '60 Seconds of Impact',
      text: "Grab attention quickly with a short, memorable intro that highlights your best qualities—boost your chances of standing out.",
    },
  ];

  const pricingTiers = [
    {
      key: 'sixMonth',
      name: CONFIG.tiers.sixMonth.name,
      price: CONFIG.tiers.sixMonth.displayPrice,
      isPopular: false,
      features: [
        `${CONFIG.tiers.sixMonth.maxUploads} Video Uploads`,
        '60-Second Recordings',
        'Standard Quality',
        '6 Months Access',
      ],
    },
    {
      key: 'lifetime',
      name: CONFIG.tiers.lifetime.name,
      price: CONFIG.tiers.lifetime.displayPrice,
      isPopular: true,
      features: [
        `${CONFIG.tiers.lifetime.maxUploads} Video Uploads`,
        '5-Minute Recordings',
        'HD Quality Video',
        'Unlimited Access',
        'Future Features',
      ],
    },
  ];

  const faqs = [
    {
      q: 'Why do I need a video introduction? Aren’t resumes and cover letters enough?',
      a: 'Resumes and cover letters tell employers what you’ve done, but a video introduction shows them who you are. In less than a minute, you can demonstrate your enthusiasm, communication skills, and personality—giving your application an edge. With ReelIntro, it’s fast and easy to get started.',
    },
    {
      q: 'I don’t have the time or skills to create a polished video introduction.',
      a: 'That’s the beauty of ReelIntro. There’s no complicated setup—just hit record on your phone or laptop, and you’re good to go. You can record a professional video introduction in just a few minutes. Plus, you can rerecord or delete your video as many times as you need before saving and sharing.',
    },
    {
      q: 'Aren’t employers more interested in traditional applications, not videos?',
      a: 'Video introductions don’t replace traditional applications—they enhance them. Think of it as your 60-second opportunity to stand out and make a personal connection with recruiters. It’s a simple, memorable way to complement your resume and cover letter.',
    },
    {
      q: 'I’m nervous about being on camera or unsure what to say.',
      a: 'That’s completely normal! ReelIntro keeps it simple so you can focus on being yourself. Just introduce who you are, why you’re excited about the role, and a quick highlight of your skills. There’s no pressure—rerecord as many times as you like until you feel good about it.',
    },
    {
      q: 'How is ReelIntro different from just recording a video on my phone?',
      a: 'ReelIntro takes the guesswork out of creating and sharing your video introduction. Record directly in our platform, preview your video, and share it with just one click. It’s fast, straightforward, and built for busy job seekers like you.',
    },
    {
      q: 'Won’t a video introduction take too much time for employers to watch?',
      a: 'Not at all! Video introductions are designed to be short—just 60 seconds or less. Employers can quickly get a sense of your personality and communication skills, making your application more memorable without taking up much of their time.',
    },
    {
      q: 'What if I make a mistake? Can I fix it?',
      a: 'Absolutely. You can rerecord or delete your video as many times as you need before saving it. The process is flexible, so you’ll always feel in control of what you share.',
    },
  ];

  const toggleFAQ = (idx) => {
    setOpenFAQIndex(openFAQIndex === idx ? null : idx);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>ReelIntro - Stand Out & Get Hired with Video Introductions</title>
        <meta name="description" content="Create professional video introductions that help your job applications stand out. Record, preview, and share your video in minutes - no technical skills needed." />
        <meta name="keywords" content="video introduction, job application, video cover letter, career video, professional introduction" />
        <link rel="canonical" href="https://reelintro.com" />
        <meta property="og:title" content="ReelIntro - Stand Out & Get Hired with Video Introductions" />
        <meta property="og:description" content="Create professional video introductions that help your job applications stand out. Record, preview, and share your video in minutes." />
        <meta property="og:url" content="https://reelintro.com" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://reelintro.com/hero-2.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ReelIntro - Stand Out & Get Hired with Video Introductions" />
        <meta name="twitter:description" content="Create professional video introductions that help your job applications stand out." />
        <meta name="twitter:image" content="https://reelintro.com/hero-2.png" />
      </Helmet>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-gray-50 opacity-70"></div>
        
        {/* Main hero content */}
        <div className="container mx-auto px-4 pt-36 pb-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="max-w-xl space-y-8 lg:pr-8">
              <div>
                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  Your Career Introduction
                </span>
                <h1 className="text-5xl md:text-6xl font-bold text-black leading-tight">{heroTitle}</h1>
                <p className="text-xl md:text-2xl text-gray-600 mt-6">{heroSubtitle}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleCTA}
                  className="bg-black text-white px-8 py-4 rounded-full font-medium hover:bg-gray-800 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  <span>Create Your First Reel:Intro</span>
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
              
              {/* Feature badges */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <div className="text-3xl font-bold text-blue-600">Easy</div>
                  <div className="text-sm text-gray-500">Simple Recording</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">60s</div>
                  <div className="text-sm text-gray-500">Perfect Length</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">Pro</div>
                  <div className="text-sm text-gray-500">Professional Results</div>
                </div>
              </div>
            </div>
            
            {/* Right: Preview/Image */}
            <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-white p-4">
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                <img 
                  src="hero-2.png" 
                  alt="Reel:Intro Preview" 
                  className="object-cover w-full h-full"
                />
              </div>
              
              {/* Feature tags below image */}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                  Create in Minutes
                </span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                  Share Instantly
                </span>
                <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium">
                  No Technical Skills Needed
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Removed trust marks section */}
      </div>

      {/* Features Section */}
      <div className="container mx-auto px-4 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Why Reel:Intro Works</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our simple platform helps you create professional video introductions that get you noticed.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-8 rounded-2xl bg-white border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.text}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Use Cases Section */}
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
              Perfect for Every Career Stage
            </span>
            <h2 className="text-3xl font-bold mb-4">Multiple Ways to Boost Your Career</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From landing your first internship to securing your dream job, Reel:Intro helps you stand out.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Job Applications */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Job Applications</h3>
              <p className="text-gray-600 mb-4">Stand out in competitive application pools by adding a personal touch to your resume and cover letter.</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Show communication skills
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Demonstrate enthusiasm
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Make a lasting impression
                </li>
              </ul>
            </div>
            
            {/* Internship Applications */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Internship Applications</h3>
              <p className="text-gray-600 mb-4">Create professional video intros for internships that require them as part of the application process.</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Meet application requirements
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Impress program coordinators
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Showcase campus involvement
                </li>
              </ul>
            </div>
            
            {/* Remote Jobs */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Remote Jobs</h3>
              <p className="text-gray-600 mb-4">Show off your virtual communication skills when applying for remote positions.</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Prove virtual presence
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Showcase tech comfort
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Connect across distance
                </li>
              </ul>
            </div>
            
            {/* Creative Professionals */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Portfolio Enhancement</h3>
              <p className="text-gray-600 mb-4">Add a personal introduction to your creative portfolio or work samples.</p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Explain your creative process
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Showcase your personality
                </li>
                <li className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Add context to your work
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Testimonials Section */}
      <div className="bg-gray-50 py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Key Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tools to help you create professional video introductions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Personal Introductions</h4>
                  <p className="text-sm text-gray-500">Video Profiles</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "Create personalized video introductions that showcase your communication skills and personality to potential employers."
              </p>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">Perfect for job applications</p>
              </div>
            </div>
            
            {/* Feature Card 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Quick & Simple</h4>
                  <p className="text-sm text-gray-500">Easy to Use</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "Our platform makes it easy to record, preview, and share your professional video introduction in minutes."
              </p>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">No technical skills required</p>
              </div>
            </div>
            
            {/* Feature Card 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h4 className="font-bold">Instant Sharing</h4>
                  <p className="text-sm text-gray-500">Easy Distribution</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "Share your professional introduction with potential employers through a simple link. No login required for viewers."
              </p>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">Include with applications</p>
              </div>
            </div>
          </div>
        </div>
      </div>

       {/* Demo Video Section */}
        {/*  <div id="demo" className="container mx-auto px-4 py-24"> 
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
              Watch How It Works
            </span>
            <h2 className="text-3xl font-bold mb-6">Create Your Video in 3 Easy Steps</h2>
            
            <div className="space-y-6">
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span className="font-bold text-blue-600">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Record Your Intro</h3>
                  <p className="text-gray-600">
                    Just hit record and create your introduction in minutes - no special equipment needed.
                  </p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span className="font-bold text-blue-600">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Preview & Save</h3>
                  <p className="text-gray-600">
                    Review your video and save it to your personal library. Re-record as many times as you want.
                  </p>
                </div>
              </div>
              
              <div className="flex">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                  <span className="font-bold text-blue-600">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Share Instantly</h3>
                  <p className="text-gray-600">
                    Get a shareable link to include with your applications. No login required for viewers.
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleCTA}
                className="mt-8 bg-black text-white px-6 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors duration-200 inline-flex items-center"
              >
                <span>Create Your First Video</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-2xl shadow-xl overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-center w-full h-full text-gray-500">
                <span>Demo Video Placeholder</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Pricing Section */}
      <div id="pricing" className="bg-gray-50 py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
              Limited Time Offer
            </span>
            <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Launch special: Lock in lifetime access at our lowest price ever. No subscriptions or hidden fees.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.key}
                className={`bg-white rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-2xl ${
                  tier.isPopular ? 'border-blue-500 scale-105 shadow-xl' : 'border-gray-200 hover:-translate-y-2'
                }`}
              >
                {tier.isPopular && (
                  <div className="text-sm font-medium bg-blue-600 text-white px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold">{tier.name}</h3>
                <div className="mt-4 mb-8">
                  <span className="text-5xl font-bold">${tier.price}</span>
                  <span className="text-gray-500 ml-2 text-lg">one-time</span>
                </div>
                
                <div className="border-t border-b border-gray-100 py-6 mb-6">
                  <ul className="space-y-4">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-5 h-5 mt-1 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button
                  onClick={() => handlePurchaseClick(tier.key)}
                  className={`
                    block w-full px-6 py-4 rounded-full font-medium text-center transition-all duration-200
                    ${tier.isPopular 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg' 
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }
                  `}
                >
                  {tier.isPopular ? 'Get Lifetime Access' : `Buy ${tier.name}`}
                </button>
                
                
              </div>
            ))}
          </div>
          
          {/* Trust badges */}
          <div className="flex justify-center items-center gap-8 mt-16">
            <div className="flex items-center text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No Monthly Fees</span>
            </div>
            
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="container mx-auto px-4 py-24">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
            Got Questions?
          </span>
          <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about creating effective video introductions.
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <div className="grid gap-6">
            {faqs.map((item, idx) => (
              <div 
                key={idx} 
                className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-200 ${
                  openFAQIndex === idx ? 'shadow-md' : ''
                }`}
              >
                <button
                  onClick={() => toggleFAQ(idx)}
                  className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
                >
                  <span className="font-bold text-lg">{item.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                    openFAQIndex === idx ? 'bg-blue-100 rotate-180' : 'bg-gray-100'
                  }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${
                      openFAQIndex === idx ? 'text-blue-600' : 'text-gray-600'
                    }`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>
                <div className={`transition-all duration-200 overflow-hidden ${
                  openFAQIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="p-6 pt-0 text-gray-600 border-t">
                    {item.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Still have questions */}
          <div className="mt-16 text-center bg-blue-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold mb-4">Still have questions?</h3>
            <p className="text-gray-600 mb-6">Get in touch with our team for personalized assistance.</p>
            <a 
              href="mailto:support@reelintro.com" 
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <span className="inline-block bg-white bg-opacity-20 text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
              Limited Time Launch Offer
            </span>
            <h2 className="text-4xl font-bold text-white">Ready to Stand Out in Your Job Applications?</h2>
            <p className="text-xl text-blue-100">
              Create your professional video introduction today and make employers remember you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button
                onClick={handleCTA}
                className="bg-white text-blue-700 px-8 py-4 rounded-full font-medium hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center shadow-lg"
              >
                <span>Get Started Now</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <button
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-medium hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
              >
                View Pricing
              </button>
            </div>
            
            {/* Info highlight */}
            <div className="mt-12 bg-white bg-opacity-10 p-8 rounded-2xl backdrop-blur-sm">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-white text-xl font-medium text-center">
                Create, preview, and share your professional video introduction in minutes
              </div>
              <div className="mt-4 text-blue-100 text-center">
                — Ready to make your application stand out?
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default Home;
